import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Fab } from 'components/v3';
import FormShellHeader from '../../components/FormShellHeader';
import { useBuilder } from '../BuilderContext';
import Canvas from '../desktop/Canvas';
import { useSaveBarSpace } from '../desktop/DesktopBuilder';
import useFieldFocus, { focusField, focusLost } from '../hooks/useFieldFocus';
import AddFieldSheet from './AddFieldSheet';
import EditPane from './EditPane';
import FieldActionSheet from './FieldActionSheet';
import SettingsPane from './SettingsPane';

// Phone builder (<768, plan §5.7), under the app's 60px bar. One pane at a
// time: the canvas (kept mounted while hidden, and its scroll position
// restored), Edit field, or Form settings. A floating + opens Add field;
// each card has Edit and a ⋯ sheet. No drag and drop.

const scrollWindow = (y) => {
  if (typeof window.scrollTo === 'function') window.scrollTo(0, y);
};

// Remember where the canvas was scrolled while another pane shows.
const useCanvasScroll = (pane) => {
  const saved = useRef(0);
  const previous = useRef(pane);
  useEffect(() => {
    if (pane !== 'canvas') return undefined;
    const onScroll = () => {
      saved.current = window.scrollY || 0;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pane]);
  useLayoutEffect(() => {
    if (previous.current === pane) return;
    previous.current = pane;
    scrollWindow(pane === 'canvas' ? saved.current : 0);
  }, [pane]);
};

// Focus follows the pane: its heading when Edit / Settings opens (or Edit
// switches to a new field, e.g. one just added from the + sheet), the
// edited card (else the canvas) when it closes. Runs after the sheets'
// own focus return, so it has the last word.
const usePaneFocus = (pane, selectedId) => {
  const headingRef = useRef(null);
  const previous = useRef({ pane, selectedId });
  useEffect(() => {
    const before = previous.current;
    previous.current = { pane, selectedId };
    if (before.pane === pane && before.selectedId === selectedId) return;
    if (pane !== 'canvas') {
      if (headingRef.current) headingRef.current.focus({ preventScroll: true });
    } else if (before.pane !== 'canvas' && focusLost()) {
      focusField(selectedId);
    }
  }, [pane, selectedId]);
  return headingRef;
};

const MobileBuilder = ({ header, banner, dirty, renderHeader }) => {
  // Keep the + clear of the save bar, which grows when it shows the live hint.
  const saveBarSpace = useSaveBarSpace(dirty);
  const { state, dispatch, readOnly, isLocked } = useBuilder();
  const locked = (id) => !!isLocked && isLocked(id);
  const [actionsFor, setActionsFor] = useState(null);
  const [adding, setAdding] = useState(false);
  const { pane } = state;
  const selected =
    state.working.fields.find((f) => f.id === state.selectedId) || null;
  useCanvasScroll(pane);
  useFieldFocus(state.working.fields);
  const headingRef = usePaneFocus(pane, state.selectedId);

  // A locked card (w9-plan §6.5) opens nothing.
  const edit = (id) => {
    if (locked(id)) return;
    dispatch({ type: 'SELECT', id, pane: 'edit' });
  };
  const openAdd = (target) => {
    dispatch({ type: 'SET_INSERT_TARGET', target: target || null });
    setAdding(true);
  };
  const toCanvas = () => dispatch({ type: 'SET_PANE', pane: 'canvas' });
  const headerProps = {
    ...header,
    onOpenSettings: () => dispatch({ type: 'SET_PANE', pane: 'settings' }),
  };
  const headerNode = renderHeader ? (
    renderHeader(headerProps)
  ) : (
    <FormShellHeader {...headerProps} />
  );

  return (
    <>
      {pane === 'canvas' ? headerNode : null}
      {pane === 'canvas' ? banner : null}
      <div
        style={{
          display: pane === 'canvas' ? 'block' : 'none',
          paddingBottom: 96,
        }}
      >
        <Canvas
          variant="mobile"
          onSelectField={edit}
          onEditField={edit}
          onMoreField={setActionsFor}
          onAddField={() => openAdd({ mode: 'end' })}
        />
      </div>
      {pane === 'edit' && selected ? (
        <EditPane
          field={selected}
          locked={locked(selected.id)}
          onMore={
            readOnly || locked(selected.id)
              ? undefined
              : () => setActionsFor(selected.id)
          }
          onDone={toCanvas}
          headingRef={headingRef}
        />
      ) : null}
      {pane === 'settings' ? (
        <SettingsPane onDone={toCanvas} headingRef={headingRef} />
      ) : null}
      <Fab
        label="Add field"
        hidden={pane !== 'canvas' || readOnly}
        bottomOffset={dirty ? saveBarSpace + 16 : 24}
        onClick={() => openAdd(null)}
      />
      <FieldActionSheet
        fieldId={actionsFor}
        includeEdit={pane !== 'edit'}
        onClose={() => setActionsFor(null)}
        onEdit={edit}
        onInsert={(target) => {
          toCanvas();
          openAdd(target);
        }}
      />
      <AddFieldSheet
        open={adding}
        mobile
        onClose={() => {
          setAdding(false);
          dispatch({ type: 'SET_INSERT_TARGET', target: null });
        }}
      />
    </>
  );
};

MobileBuilder.propTypes = {
  header: PropTypes.object.isRequired,
  banner: PropTypes.node,
  dirty: PropTypes.bool,
  // (headerProps) → node, in place of FormShellHeader (w9-plan §6.5).
  renderHeader: PropTypes.func,
};

MobileBuilder.defaultProps = {
  banner: null,
  dirty: false,
  renderHeader: undefined,
};

export default MobileBuilder;
